import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormComponent, FormComponentProps } from '../../../../components/form';
import { CropWorkType } from '../../../../enums/crop-work-types';
import cssCheckbox from './../../../../components/form/elements/checkbox.module.css';
import css from './product-select.module.css';
import Checkbox from '../../../../components/form/elements/checkbox';
import Warehouse from '../../../../interfaces/warehouse';

export default function ProductSelect(
  product: {
    name: string;
    title?: string;
    components: () => FormComponent[];
  },
  warehouse: {
    name: string;
    title?: string;
    components: () => FormComponent[];
  },
  enablerCheckbox: { label: string; checked: boolean } = null,
  disableComponents?: boolean
): FormComponent {
  return ({ ...rest }) => (
    <ProductSelectComponent
      product={product}
      warehouse={warehouse}
      enablerCheckbox={enablerCheckbox}
      disableComponents={disableComponents}
      {...rest}
    />
  );
}

export function ProductSelectComponent({
  product,
  warehouse,
  enablerCheckbox,
  disableComponents,
  parentName,
  formValues,
  ...rest
}: FormComponentProps & {
  product: { name: string; title?: string; components: () => FormComponent[] };
  warehouse: {
    name: string;
    title?: string;
    components: () => FormComponent[];
  };
  enablerCheckbox?: { label: string; checked: boolean };
  disableComponents?: boolean;
  parentName?: string;
  formValues?: object;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );

  const [enabled, setEnabled] = useState<boolean>(
    enablerCheckbox ? enablerCheckbox.checked : true
  );

  const { trigger, getValues, setValue } = useFormContext();
  const emptyProductState = {
    count: 0,
    products: {} as {
      [key: string]: {
        components: FormComponent[];
        warehouseCount: number;
        warehouses: {
          [key: string]: {
            components: FormComponent[];
          };
        };
        IsInterculture: FormComponent;
      };
    },
  };

  const products = useWatch({ name: 'products' });
  const [allowEnablerCheckbox, setAllowEnablerCheckbox] = useState<boolean>();

  useEffect(() => {
    if (products) {
      Object.values(products).findIndex((a: any) =>
        a ? a.is_interculture : null
      ) !== -1
        ? setAllowEnablerCheckbox(true)
        : setAllowEnablerCheckbox(false);
    }

    if (allowEnablerCheckbox && product.name === 'intercrop') {
      setValue('intercrop', []);
      trigger('intercrop');
      setEnabled(false);
    }
  }, [
    products,
    product.name,
    enablerCheckbox,
    allowEnablerCheckbox,
    enabled,
    setValue,
    trigger,
  ]);

  const [state, setState] = useState(emptyProductState);

  const addProduct = useCallback(() => {
    setState((state) => {
      let warehouses = {} as {
        [key: string]: {
          components: FormComponent[];
        };
      };
      const warehouseCount =
        formValues[product.name]?.[state.count.toString()]?.[warehouse.name]
          ?.length || 1;
      for (let i = 0; i < warehouseCount; i++) {
        warehouses[i.toString()] = { components: warehouse.components() };
      }
      state.products[state.count.toString()] = {
        components: product.components(),
        warehouseCount: warehouseCount,
        warehouses: warehouses,
        IsInterculture: Checkbox('is_interculture', tp('interculture')),
      };
      return {
        ...state,
        count: state.count + 1,
        products: state.products,
      };
    });
  }, [formValues, product, warehouse, tp]);

  const addWarehouse = useCallback(
    (productKey: string) => {
      const count = state.products[productKey].warehouseCount;
      state.products[productKey].warehouseCount = count + 1;
      state.products[productKey].warehouses[count.toString()] = {
        components: warehouse.components(),
      };

      setState((state) => {
        return {
          ...state,
          products: state.products,
        };
      });
    },
    [state.products, warehouse]
  );

  const removeProduct = (rowIndex: string) => {
    let products = getValues('products');
    delete products[rowIndex];
    setValue('products', products);

    trigger('products');

    setState((state) => {
      delete state.products[rowIndex];
      return { ...state };
    });
  };

  const removeWarehouse = (productKey: string, rowIndex: string) => {
    const warehouses = 'products.' + productKey + '.warehouses';

    setValue(
      warehouses,
      getValues(warehouses).filter(
        (_warehouse: Warehouse, index: number) => index !== +rowIndex
      )
    );

    trigger(warehouses);

    setState((state) => {
      delete state.products[productKey].warehouses[rowIndex];
      return { ...state };
    });
  };

  useEffect(() => {
    if (state.count !== 0 && !enabled) {
      setState(emptyProductState);
      setTimeout(() => {
        trigger();
      }, 500);
    }
    // TODO: find a better solution
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (state.count !== 0) {
      return;
    }
    const productCount = formValues[product.name]?.length || 1;
    for (let i = 0; i < productCount; i++) {
      setState((state) => {
        if (state.count < productCount) {
          let warehouses = {} as {
            [key: string]: {
              components: FormComponent[];
            };
          };
          const warehouseCount =
            formValues[product.name]?.[state.count.toString()]?.[warehouse.name]
              ?.length || 1;
          for (let i = 0; i < warehouseCount; i++) {
            warehouses[i.toString()] = { components: warehouse.components() };
          }
          state.products[state.count.toString()] = {
            components: product.components(),
            warehouseCount: warehouseCount,
            warehouses: warehouses,
            IsInterculture: Checkbox('is_interculture', tp('interculture')),
          };
          return {
            ...state,
            count: state.count + 1,
            products: state.products,
          };
        } else {
          return { ...state };
        }
      });
    }
  }, [state.count, formValues, product, addProduct, warehouse, tp]);

  const body = () => {
    return (
      <>
        {product.title && <h3>{product.title}</h3>}
        {Object.keys(state.products).map((productKey) => (
          <fieldset
            key={product.name.concat('-', productKey)}
            className={css['product']}
          >
            <div className={css['body']}>
              {productKey !== '0' && (
                <div className={css['remove']}>
                  <button
                    type="button"
                    onClick={() => removeProduct(productKey)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
              <div className={css['body_top']}>
                {productKey === '0' &&
                  formValues.type_id === CropWorkType.SOWING_OR_PLANTING && (
                    <h3 className={css['title']}>
                      {allowEnablerCheckbox && disableComponents
                        ? tp('interculture')
                        : tp('main_product')}
                    </h3>
                  )}
                {!disableComponents &&
                  product.name !== 'intercrop' &&
                  formValues.type_id === CropWorkType.SOWING_OR_PLANTING && (
                    <div className={css['checkbox']}>
                      {[state.products[productKey]].map((Child, index) => (
                        <Child.IsInterculture
                          key={`${product.name}-${productKey}-${index}`}
                          parentName={`${
                            parentName ? parentName.concat('.') : ''
                          }${product.name}.${productKey}`}
                        />
                      ))}
                    </div>
                  )}
              </div>
              <div className={css['components']}>
                {state.products[productKey].components.map((Child, index) => {
                  return (
                    <Child
                      key={product.name.concat(
                        '-',
                        productKey,
                        '-',
                        index.toString()
                      )}
                      parentName={(parentName
                        ? parentName.concat('.')
                        : ''
                      ).concat(product.name, '.', productKey)}
                      {...rest}
                    />
                  );
                })}
              </div>
            </div>
            <div className={css['warehouses']}>
              {warehouse.title && <h4>{warehouse.title}</h4>}
              {Object.keys(state.products[productKey].warehouses).map(
                (warehouseKey, index) => (
                  <fieldset
                    key={product.name.concat(
                      '-',
                      productKey,
                      '-',
                      warehouse.name,
                      '-',
                      warehouseKey
                    )}
                    className={css['warehouse']}
                  >
                    <div className={css['body']}>
                      <div className={css['remove']}>
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              removeWarehouse(productKey, warehouseKey)
                            }
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                      <div className={css['components']}>
                        {state.products[productKey].warehouses[
                          warehouseKey
                        ].components.map((Child, index) => {
                          return (
                            <Child
                              key={product.name.concat(
                                '-',
                                productKey,
                                '-',
                                warehouseKey,
                                '-',
                                index.toString()
                              )}
                              parentName={(parentName
                                ? parentName.concat('.')
                                : ''
                              ).concat(
                                product.name,
                                '.',
                                productKey,
                                '.',
                                warehouse.name,
                                '.',
                                warehouseKey
                              )}
                              {...rest}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </fieldset>
                )
              )}
              <div className={css['add']}>
                <button type="button" onClick={() => addWarehouse(productKey)}>
                  <FontAwesomeIcon icon={faPlus} /> {tp('add_warehouse')}
                </button>
              </div>
            </div>
          </fieldset>
        ))}
        {!disableComponents ? (
          <div className={css['add']}>
            <button type="button" onClick={addProduct}>
              <FontAwesomeIcon icon={faPlus} /> {tp('add_product')}
            </button>
          </div>
        ) : (
          ''
        )}
      </>
    );
  };

  const onCheckboxChange = () => {
    if (enabled) {
      setEnabled(false);
      setValue('intercrop', []);
      trigger('intercrop');
    } else {
      setEnabled(true);
    }
  };

  return (
    <div className={css['products']}>
      <div>
        {!allowEnablerCheckbox && enablerCheckbox && (
          <>
            <input
              id={`${product.name}_enabled`}
              type="checkbox"
              checked={enabled}
              className={cssCheckbox['checkbox']}
              onChange={onCheckboxChange}
            />
            <label htmlFor={`${product.name}_enabled`}>
              {enablerCheckbox.label}
            </label>
          </>
        )}
      </div>
      {enabled && body()}
    </div>
  );
}
